/*
 * Cross-sell "Add to your order"
 * ------------------------------------------------------------------
 * A reusable add-on offer on the product page. When the customer keeps
 * the box ticked, the add-on rides along as a normal, separate cart line
 * through the existing cart and the existing checkout — one payment, one
 * delivery, independent inventory.
 *
 * The <product-cross-sell> element only carries state (its variant, its
 * checkbox, whether it is already in the cart). A single set of document
 * level listeners does the work so that:
 *   - "Add to cart" adds both items in one atomic /cart/add request, and
 *   - "Buy it now" (dynamic checkout) is intercepted so the add-on is not
 *     skipped when the button bypasses the cart.
 *
 * Nothing here rewrites the theme's product-form.js: on "Add to cart" we
 * simply reshape the form into a multi-item add just before the theme's
 * own handler posts it, then restore the form afterwards.
 */
(function () {
  if (window.__crossSellInit) return;
  window.__crossSellInit = true;

  var ADDED_FROM_VALUE = 'product-page';

  // formId -> Set(<product-cross-sell>)
  var registry = new Map();

  function register(el) {
    var id = el.formId;
    if (!id) return;
    if (!registry.has(id)) registry.set(id, new Set());
    registry.get(id).add(el);
  }
  function unregister(el) {
    var set = registry.get(el.formId);
    if (set) set.delete(el);
  }

  // NB: read the id *attribute*, not form.id — a product form contains
  // <input name="id"> (the variant id), and named form controls shadow the
  // HTMLFormElement.id property, so form.id would return that input element.
  function formIdOf(form) {
    return form.getAttribute('id');
  }

  function findQuantityInput(form) {
    // The quantity input lives outside the <form> and is linked via form=""
    return (
      form.querySelector('[name="quantity"]') ||
      document.querySelector('[name="quantity"][form="' + formIdOf(form) + '"]')
    );
  }

  function pendingAddons(form) {
    var set = registry.get(formIdOf(form));
    if (!set) return [];
    return Array.from(set).filter(function (el) {
      return el.isChecked && !el.addonInCart && el.variantId;
    });
  }

  // ---- Add to cart: reshape into a single multi-item add ----------------
  function cleanupForm(form) {
    form.querySelectorAll('[data-cross-sell-injected]').forEach(function (n) {
      n.remove();
    });
    if (form.__crossSellDisabled) {
      form.__crossSellDisabled.forEach(function (f) {
        f.disabled = false;
      });
      form.__crossSellDisabled = null;
    }
  }

  function onSubmitCapture(evt) {
    var form = evt.target;
    if (!form || form.tagName !== 'FORM' || !registry.has(formIdOf(form))) return;

    cleanupForm(form); // clear anything stale from a previous submit

    var addons = pendingAddons(form);
    if (!addons.length) return; // box unticked or already in cart -> normal single add

    var idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return; // no variant selected -> let theme handle it
    var qtyInput = findQuantityInput(form);

    var injected = [];
    function addHidden(name, value) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('data-cross-sell-injected', '');
      form.appendChild(input);
      injected.push(input);
    }

    // items[0] = the main product (current variant + chosen quantity)
    addHidden('items[0][id]', idInput.value);
    addHidden('items[0][quantity]', qtyInput && qtyInput.value ? qtyInput.value : '1');

    // items[1..n] = each ticked add-on, always quantity 1
    addons.forEach(function (el, i) {
      var n = i + 1;
      addHidden('items[' + n + '][id]', el.variantId);
      addHidden('items[' + n + '][quantity]', '1');
      addHidden('items[' + n + '][properties][_added_from]', ADDED_FROM_VALUE);
      el.addonInCart = true; // optimistic; refreshed on cart-update
    });

    // Exclude the singular id/quantity so /cart/add treats this as multi-item
    idInput.disabled = true;
    if (qtyInput) qtyInput.disabled = true;
    form.__crossSellDisabled = [idInput, qtyInput].filter(Boolean);

    // Restore once the theme has posted (cart-update), with a timeout fallback
    var restore = function () {
      cleanupForm(form);
    };
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      var off = subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        restore();
        if (off) off();
      });
    }
    setTimeout(restore, 3000);
    // No preventDefault: the theme's product-form handler runs next and posts.
  }

  // "Buy it now" is handled by buy-it-now.js (a custom button that goes
  // straight to /checkout). It reads each ticked <product-cross-sell> and its
  // addonInCart flag directly, so no handler is needed here.

  document.addEventListener('submit', onSubmitCapture, true);

  // ---- The element: state only ------------------------------------------
  if (!customElements.get('product-cross-sell')) {
    customElements.define(
      'product-cross-sell',
      class ProductCrossSell extends HTMLElement {
        connectedCallback() {
          this.formId = this.dataset.productFormId;
          this.variantId = this.dataset.variantId;
          this.checkbox = this.querySelector('[data-cross-sell-checkbox]');
          this.addonInCart = false;

          register(this);

          // The cart drawer's DOM always mirrors the cart and is re-rendered on
          // every add/remove/quantity change. Deriving state from it needs no
          // network call (rate-limit proof) and — crucially for this theme — it
          // also catches drawer removals, which do NOT publish a cart-update
          // event. Fall back to /cart.js + cart-update only when there's no drawer.
          this.drawer = document.querySelector('cart-drawer');
          if (this.drawer) {
            this.syncFromDrawer();
            var self = this;
            var timer;
            this._observer = new MutationObserver(function () {
              clearTimeout(timer);
              timer = setTimeout(function () {
                self.syncFromDrawer();
              }, 150);
            });
            this._observer.observe(this.drawer, { childList: true, subtree: true });
          } else {
            this.fetchCartState();
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchCartState.bind(this));
            }
          }
        }

        disconnectedCallback() {
          unregister(this);
          if (this._unsub) this._unsub();
          if (this._observer) this._observer.disconnect();
        }

        get isChecked() {
          return !!(this.checkbox && this.checkbox.checked);
        }

        // Is the add-on's variant present as a line in the cart drawer DOM?
        syncFromDrawer() {
          if (!this.drawer) return;
          var v = String(this.variantId);
          this.addonInCart = !!this.drawer.querySelector(
            '[data-quantity-variant-id="' + v + '"], [data-variant-id="' + v + '"]'
          );
        }

        fetchCartState() {
          var self = this;
          var cartUrl = (window.routes && routes.cart_url) || '/cart';
          fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              var v = String(self.variantId);
              self.addonInCart = ((cart && cart.items) || []).some(function (i) {
                return String(i.variant_id) === v;
              });
            })
            .catch(function () {});
        }
      }
    );
  }
})();
